import EmptyState from '@/components/EmptyState';
import { InfoTip } from '@/components/InfoTip';
import {
  useReservationConfig,
  useUpdateReservationConfig,
} from '@/features/reservation-setup/hooks/useReservationConfig';
import { endOfTodayIso, isPauseActive } from '@/utils/datetime';
import { ReservationConfigUpdateSchema } from '@restaurant/api-contracts';
import type { ReservationConfigUpdateBody } from '@restaurant/api-client';
import {
  Block,
  BlockFooter,
  BlockTitle,
  List,
  ListItem,
  Navbar,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import {} from 'react';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import { useT } from '@/i18n';

const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

const MAX_CLOSING_BUFFER_MINUTES = 24 * 60;

export default function ReservationSetupPage() {
  const t = useT();
  const rs = t.reservationSetup;

  // Localized "table held for" duration. Closes over `rs` so the locale slice is
  // never undefined at the call site.
  const fmtMinutes = (total: number): string => {
    if (total <= 0) return rs.fmtNone;
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h && m) return rs.fmtHoursMinutes(h, m);
    if (h) return rs.fmtHours(h);
    return rs.fmtMinutes(m);
  };
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: r, isLoading, isError } = useReservationConfig();
  const update = useUpdateReservationConfig();

  // Validate the single-field patch against the contract, then save. Problems
  // surface as a toast - never a blocking alert.
  const commit = (patch: ReservationConfigUpdateBody, done: string) => {
    const parsed = ReservationConfigUpdateSchema.safeParse(patch);
    if (!parsed.success) {
      toast(parsed.error.issues[0]?.message ?? rs.invalidValue);
      return;
    }
    update.mutate(parsed.data, {
      onSuccess: () => toast(done),
      onError: (e) => toast(e instanceof Error ? e.message : rs.saveFailed),
    });
  };

  const askInt = (opts: {
    title: string;
    message: string;
    current: number;
    min: number;
    max?: number;
    apply: (n: number) => ReservationConfigUpdateBody;
    validate?: (n: number) => string | null;
    done: string;
  }) => {
    f7.dialog.prompt(
      opts.message,
      opts.title,
      (value) => {
        const n = Number.parseInt(value.trim(), 10);
        if (!Number.isFinite(n) || n < opts.min) {
          toast(rs.enterWholeNumber(opts.min));
          return;
        }
        if (opts.max !== undefined && n > opts.max) {
          toast(rs.enterWholeNumberBetween(opts.min, opts.max));
          return;
        }
        const problem = opts.validate?.(n);
        if (problem) {
          toast(problem);
          return;
        }
        commit(opts.apply(n), opts.done);
      },
      undefined,
      String(opts.current),
    );
  };

  const chooseFrom = (
    title: string,
    options: { text: string; patch: ReservationConfigUpdateBody; done: string }[],
  ) => {
    f7.actions
      .create({
        buttons: [
          [
            { label: true, text: title },
            ...options.map((o) => ({
              text: o.text,
              onClick: () => commit(o.patch, o.done),
            })),
          ],
          [{ text: t.cancel, color: 'red' }],
        ],
      })
      .open();
  };

  const editLargePartyMsg = () => {
    if (!r) return;
    f7.dialog.prompt(
      rs.shownToGuestsAboveMax,
      rs.largePartyMessage,
      (value) => {
        const text = value.trim();
        commit({ largePartyMsg: text || null }, rs.messageUpdated);
      },
      undefined,
      r.largePartyMsg ?? '',
    );
  };

  // Binary settings render as an inline iOS switch (HIG → Toggles): flipping it
  // commits immediately, with the result confirmed by a toast. No tap-through
  // dialog - the switch IS the control and its position IS the state.
  //
  // Plain controlled checkbox with F7's `.toggle` markup instead of the F7
  // <Toggle> component: <Toggle> relies on its F7 instance firing `toggleChange`
  // to push state back, and in this settings master-detail / re-rendering list
  // that event sometimes never wires, so taps did nothing. Here React owns the
  // checkbox directly - the real onChange always follows the tap. (Mirrors the
  // OpeningHoursPage fix.)
  const toggleRow = (opts: {
    title: string;
    tooltip: string;
    ariaLabel: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
  }) => (
    <ListItem title={opts.title}>
      <InfoTip
        slot="after-title"
        aria-label={opts.ariaLabel}
        text={opts.tooltip}
        style={{ marginLeft: 6 }}
      />
      <label className="toggle" slot="after">
        <input
          type="checkbox"
          checked={opts.checked}
          onChange={(e) => opts.onChange(e.target.checked)}
        />
        <span className="toggle-icon" />
      </label>
    </ListItem>
  );

  // A tap-to-edit row whose current value sits on a SECOND LINE beneath the title.
  // F7React drops `subtitle`/`text`/`slot="subtitle"` when the item has children,
  // so we render the whole two-line block inside `slot="title"` (which always
  // renders) and own the layout. Title keeps full row width (never truncated on
  // mobile); value shows clearly under it. `muted` dims placeholder/“none” values.
  const valueRow = (opts: {
    title: string;
    tooltip: string;
    ariaLabel: string;
    value: string;
    muted?: boolean;
    onClick: () => void;
  }) => (
    <ListItem
      link="#"
      onClick={opts.onClick}
      className={`rsv-value-row${opts.muted ? ' rsv-value-row--muted' : ''}`}
    >
      <div slot="title" className="rsv-stack">
        <div className="rsv-stack-head">
          <span className="rsv-stack-title">{opts.title}</span>
          <InfoTip aria-label={opts.ariaLabel} text={opts.tooltip} style={{ marginLeft: 6 }} />
        </div>
        <span className="rsv-stack-value">{opts.value}</span>
      </div>
    </ListItem>
  );

  return (
    <Page name="reservation-setup">
      <Navbar title={rs.title} backLink={settingsBackLink} />

      {isLoading && (
        <Block style={{ textAlign: 'center', marginTop: 40 }}>
          <Preloader />
        </Block>
      )}

      {!isLoading && (isError || !r) && (
        <Block>
          <EmptyState
            icon="exclamationmark_triangle"
            title={rs.couldntLoadSettings}
            description={rs.checkConnection}
          />
        </Block>
      )}

      {!isLoading && !isError && r && (
        <>
          <BlockTitle className="rsv-section-title">{rs.bookings}</BlockTitle>
          <BlockFooter className="rsv-section-footer">{rs.bookingsFooter}</BlockFooter>
          <div className="rsv-setup-card">
            <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
              {toggleRow({
                title: rs.enableBookings,
                ariaLabel: rs.enableBookingsAria,
                tooltip: rs.enableBookingsTooltip,
                checked: r.enabled,
                onChange: (checked) =>
                  commit(
                    { enabled: checked },
                    checked ? rs.reservationsTurnedOn : rs.reservationsTurnedOff,
                  ),
              })}
              {toggleRow({
                title: rs.autoConfirmBookings,
                ariaLabel: rs.autoConfirmAria,
                tooltip: rs.autoConfirmTooltip,
                checked: r.autoConfirmMode === 'AUTO',
                onChange: (checked) =>
                  commit(
                    { autoConfirmMode: checked ? 'AUTO' : 'MANUAL' },
                    checked ? rs.bookingsAutoConfirmedToast : rs.bookingsNeedReview,
                  ),
              })}
              {toggleRow({
                title: rs.pauseToday,
                ariaLabel: rs.pauseTodayAria,
                tooltip: rs.pauseTodayTooltip,
                checked: isPauseActive(r.pausedUntil),
                onChange: (checked) =>
                  commit(
                    { pausedUntil: checked ? endOfTodayIso() : null },
                    checked ? rs.bookingsPausedToday : rs.bookingsResumed,
                  ),
              })}
            </List>
          </div>

          <BlockTitle className="rsv-section-title">{rs.bookingForm}</BlockTitle>
          <BlockFooter className="rsv-section-footer">{rs.bookingFormFooter}</BlockFooter>
          <div className="rsv-setup-card">
            <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
              {toggleRow({
                title: rs.occasionField,
                ariaLabel: rs.occasionFieldAria,
                tooltip: rs.occasionFieldTooltip,
                checked: r.occasionEnabled,
                onChange: (checked) =>
                  commit(
                    { occasionEnabled: checked },
                    checked ? rs.occasionFieldShown : rs.occasionFieldHidden,
                  ),
              })}
              {toggleRow({
                title: rs.seatingAccessField,
                ariaLabel: rs.seatingAccessAria,
                tooltip: rs.seatingAccessTooltip,
                checked: r.seatingPreferenceEnabled,
                onChange: (checked) =>
                  commit(
                    { seatingPreferenceEnabled: checked },
                    checked ? rs.seatingAccessShown : rs.seatingAccessHidden,
                  ),
              })}
            </List>
          </div>

          <BlockTitle className="rsv-section-title">{rs.partySize}</BlockTitle>
          <BlockFooter className="rsv-section-footer">{rs.partySizeFooter}</BlockFooter>
          <div className="rsv-setup-card">
            <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
              {valueRow({
                title: rs.largestParty,
                tooltip: rs.largestPartyTooltip,
                ariaLabel: rs.largestPartyAria,
                value: rs.guestsCount(r.maxPartySize),
                onClick: () =>
                  askInt({
                    title: rs.largestParty,
                    message: rs.maxGuestsPerBooking,
                    current: r.maxPartySize,
                    min: 1,
                    apply: (n) => ({ maxPartySize: n }),
                    done: rs.partySizeUpdated,
                  }),
              })}
              {valueRow({
                title: rs.largePartyMessage,
                tooltip: rs.largePartyMessageTooltip,
                ariaLabel: rs.largePartyMessageAria,
                value: r.largePartyMsg || rs.largePartyMessageHidden,
                muted: !r.largePartyMsg,
                onClick: editLargePartyMsg,
              })}
            </List>
          </div>

          <BlockTitle className="rsv-section-title">{rs.bookingWindow}</BlockTitle>
          <BlockFooter className="rsv-section-footer">{rs.bookingWindowFooter}</BlockFooter>
          <div className="rsv-setup-card">
            <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
              {valueRow({
                title: rs.guestsCanBookUpTo,
                tooltip: rs.bookingHorizonTooltip,
                ariaLabel: rs.bookingHorizonAria,
                value: rs.daysAhead(r.advanceWindow),
                onClick: () =>
                  askInt({
                    title: rs.bookingHorizon,
                    message: rs.daysAheadGuests,
                    current: r.advanceWindow,
                    min: 1,
                    apply: (n) => ({ advanceWindow: n }),
                    done: rs.bookingWindowUpdated,
                  }),
              })}
              {valueRow({
                title: rs.lastMinuteCutoffMinutes,
                tooltip: rs.lastMinuteCutoffTooltip,
                ariaLabel: rs.lastMinuteCutoffAria,
                value: r.cutoffTime === 0 ? rs.untilStart : rs.minBefore(r.cutoffTime),
                muted: r.cutoffTime === 0,
                onClick: () =>
                  askInt({
                    title: rs.lastMinuteCutoffMinutes,
                    message: rs.lastMinuteCutoffMsg,
                    current: r.cutoffTime,
                    min: 0,
                    apply: (n) => ({ cutoffTime: n }),
                    done: rs.cutoffUpdated,
                  }),
              })}
              {valueRow({
                title: rs.slotSpacing,
                tooltip: rs.slotSpacingTooltip,
                ariaLabel: rs.slotSpacingAria,
                value: rs.everyMinShort(r.slotInterval),
                onClick: () =>
                  chooseFrom(
                    rs.minutesBetweenBookableTimes,
                    [15, 30, 45, 60].map((m) => ({
                      text: rs.everyMinutes(m),
                      patch: { slotInterval: m },
                      done: rs.slotSpacingUpdated,
                    })),
                  ),
              })}
              {valueRow({
                title: rs.lastBookingBeforeClose,
                tooltip: rs.lastBookingBeforeCloseTooltip,
                ariaLabel: rs.lastBookingBeforeCloseAria,
                value:
                  r.closingBufferMinutes === 0
                    ? rs.upToClose
                    : rs.minBefore(r.closingBufferMinutes),
                muted: r.closingBufferMinutes === 0,
                onClick: () =>
                  askInt({
                    title: rs.lastBookingBeforeClose,
                    message: rs.stopBookingsBeforeClose,
                    current: r.closingBufferMinutes,
                    min: 0,
                    max: MAX_CLOSING_BUFFER_MINUTES,
                    apply: (n) => ({ closingBufferMinutes: n }),
                    done: rs.lastBookingBeforeCloseUpdated,
                  }),
              })}
            </List>
          </div>

          <BlockTitle className="rsv-section-title">{rs.tableTiming}</BlockTitle>
          <BlockFooter className="rsv-section-footer">{rs.tableTimingFooter}</BlockFooter>
          <div className="rsv-setup-card">
            <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
              {valueRow({
                title: rs.defaultTableTime,
                tooltip: rs.defaultTableTimeTooltip,
                ariaLabel: rs.defaultTableTimeAria,
                value: fmtMinutes(r.duration),
                onClick: () =>
                  askInt({
                    title: rs.defaultTableTime,
                    message: rs.defaultTableTimeMsg,
                    current: r.duration,
                    min: 15,
                    apply: (n) => ({ duration: n }),
                    done: rs.tableTimeUpdated,
                  }),
              })}
            </List>
          </div>

          <BlockTitle className="rsv-section-title">{rs.noShows}</BlockTitle>
          <BlockFooter className="rsv-section-footer">{rs.noShowsFooter}</BlockFooter>
          <div className="rsv-setup-card">
            <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
              {valueRow({
                title: rs.noShowGracePeriod,
                tooltip: rs.noShowGracePeriodTooltip,
                ariaLabel: rs.noShowGracePeriodAria,
                value: rs.minCount(r.gracePeriod),
                onClick: () =>
                  askInt({
                    title: rs.noShowGracePeriod,
                    message: rs.noShowGracePeriodMsg,
                    current: r.gracePeriod,
                    min: 0,
                    apply: (n) => ({ gracePeriod: n }),
                    done: rs.gracePeriodUpdated,
                  }),
              })}
            </List>
          </div>
        </>
      )}
    </Page>
  );
}
